import { Box, Flex, SimpleGrid, TagsInput, Text, TextInput } from "@mantine/core";
import { Dispatch, SetStateAction } from "react";
import { CategoryTreeSelect } from "../CategoryTreeSelect";
import { SectionCard } from "../HomeBlockShared";
import { PersonaCard, PersonaConfig, PersonaSuggestion } from "../homeBlockConfig";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import {
  AccordionList,
  applyAction,
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  IconLabelList,
  iconLabelItem,
  IconPicker,
  ImagePicker,
  lucideIcon,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.persona;
const MAX_SUGGESTIONS = 3;

type SampleCard = {
  title: string;
  icon: string;
  bullets: string[];
  // [tên gợi ý, icon Lucide]; tên cũng dùng để tự khớp danh mục
  suggestions: [string, string][];
  link: string;
  x: number;
};

// Dữ liệu mẫu theo thiết kế. Ảnh cắt từ thiết kế chỉ để xem khi chưa tải ảnh thẻ, không lưu.
const SAMPLE_CARDS: SampleCard[] = [
  {
    title: "Sinh viên",
    icon: "graduation-cap",
    bullets: ["Học tập hiệu quả", "Lập trình, làm đồ án", "Sẵn sàng cho tương lai"],
    suggestions: [["Laptop", "laptop"], ["Tablet", "tablet"], ["Phụ kiện", "mouse"]],
    link: "/may-tinh-cho-sinh-vien",
    x: 10,
  },
  {
    title: "Game thủ",
    icon: "gamepad-2",
    bullets: ["Hiệu năng đỉnh cao", "Chiến mọi tựa game", "Trải nghiệm mượt mà"],
    suggestions: [["Gaming Laptop", "laptop"], ["Gaming PC", "pc-case"], ["Gaming Gear", "gamepad-2"]],
    link: "/gaming",
    x: 170,
  },
  {
    title: "Dân văn phòng",
    icon: "briefcase",
    bullets: ["Làm việc hiệu quả", "Di chuyển linh hoạt", "Cân bằng cuộc sống"],
    suggestions: [["Laptop", "laptop"], ["PC để bàn", "pc-case"], ["Màn hình", "monitor"]],
    link: "/van-phong",
    x: 328,
  },
  {
    title: "Designer sáng tạo",
    icon: "palette",
    bullets: ["Thiết kế 2D/3D chuyên nghiệp", "Màu sắc chuẩn xác", "Sáng tạo không giới hạn"],
    suggestions: [["Laptop đồ họa", "laptop"], ["Màn hình đồ họa", "monitor"], ["Bảng vẽ", "palette"]],
    link: "/do-hoa",
    x: 485,
  },
  {
    title: "Video Editor",
    icon: "video",
    bullets: ["Dựng phim mượt mà", "Xử lý video 4K/8K", "Hiệu ứng chuyên nghiệp"],
    suggestions: [["Workstation", "pc-case"], ["Màn hình 4K", "monitor"], ["Lưu trữ", "hard-drive"]],
    link: "/dung-phim",
    x: 643,
  },
  {
    title: "Kiến trúc sư",
    icon: "ruler",
    bullets: ["Thiết kế không giới hạn", "Render nhanh, mượt", "Kiến tạo không gian sống"],
    suggestions: [["Workstation", "pc-case"], ["Màn hình lớn", "monitor"], ["Phần mềm", "app-window"]],
    link: "/kien-truc",
    x: 802,
  },
  {
    title: "AI User",
    icon: "sparkles",
    bullets: ["Sức mạnh AI, Machine Learning", "Xử lý dữ liệu lớn", "Dẫn đầu xu hướng"],
    suggestions: [["AI Laptop", "laptop"], ["AI PC", "pc-case"], ["GPU & Linh kiện", "gpu"]],
    link: "/ai",
    x: 960,
  },
  {
    title: "Streamer",
    icon: "mic",
    bullets: ["Livestream mượt mà", "Thu hút mọi ánh nhìn", "Sáng tạo nội dung chuyên nghiệp"],
    suggestions: [["Streaming PC", "pc-case"], ["Webcam", "webcam"], ["Micro", "mic"]],
    link: "/streaming",
    x: 1118,
  },
];

const sampleCardId = (index: number) => `persona-sample-${index + 1}`;
const SAMPLE_CROPS = new Map<string, Crop>(
  SAMPLE_CARDS.map((s, i) => [sampleCardId(i), { x: s.x, y: 91, w: 150, h: 150 }])
);

const emptySuggestion = (): PersonaSuggestion => ({
  id: newId(),
  icon: null,
  category: { id: null, name: "" },
  label: "",
});

const initialConfig = (block: HomeBlock): PersonaConfig => {
  const sample = block.code === "home-by-profession";
  return {
    heading: {
      title: sample ? "Chọn đúng máy" : block.name,
      highlight: sample ? "theo nghề nghiệp" : "",
      description: sample
        ? "Mỗi công việc đều có giải pháp công nghệ phù hợp. Khám phá ngay cùng HACOM!"
        : "",
      promises: sample
        ? [
            iconLabelItem("truck", "Giao nhanh toàn quốc"),
            iconLabelItem("shield-check", "Bảo hành chính hãng"),
            iconLabelItem("credit-card", "Trả góp linh hoạt"),
          ]
        : [],
    },
    cards: sample
      ? SAMPLE_CARDS.map(
          (s, i): PersonaCard => ({
            id: sampleCardId(i),
            title: s.title,
            image: null,
            icon: lucideIcon(s.icon),
            bullets: s.bullets,
            // id danh mục chưa biết: ô chọn danh mục tự khớp theo tên khi tải xong cây danh mục
            suggestions: s.suggestions.map(([label, icon]) => ({
              id: newId(),
              icon: lucideIcon(icon),
              category: { id: null, name: label },
              label,
            })),
            buttonLabel: "Khám phá ngay",
            link: s.link,
          })
        )
      : [],
  };
};

const suggestionText = (s: PersonaSuggestion) => s.label || s.category.name;

const EditPersona = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading, cards } = config;

  const patchHeading = (v: Partial<PersonaConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));

  // Cập nhật gợi ý của đúng một thẻ theo dạng hàm, để nhiều ô tự khớp danh mục cùng lúc không ghi đè nhau
  const suggestionSetter =
    (cardId: string): Dispatch<SetStateAction<PersonaSuggestion[]>> =>
    (action) =>
      setConfig((c) => ({
        ...c,
        cards: c.cards.map((card) =>
          card.id === cardId
            ? {
                ...card,
                suggestions: typeof action === "function" ? action(card.suggestions) : action,
              }
            : card
        ),
      }));

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
          <TextInput
            label="Mô tả"
            value={heading.description}
            onChange={(e) => patchHeading({ description: e.currentTarget.value })}
          />
        </SimpleGrid>
        <IconLabelList
          label="Cam kết nhỏ góc phải đầu block"
          items={heading.promises}
          onChange={(action) =>
            setConfig((c) => ({
              ...c,
              heading: { ...c.heading, promises: applyAction(action, c.heading.promises) },
            }))
          }
          max={3}
          addLabel="Thêm cam kết"
        />
      </SectionCard>

      <SectionCard title="Thẻ nhóm khách hàng">
        <AccordionList
          items={cards}
          onChange={fieldSetter(setConfig, "cards")}
          itemLabel={(c) => c.title}
          renderHead={(c, i, update) => {
            const crop = SAMPLE_CROPS.get(c.id);
            return {
              title: c.title,
              subtitle: c.suggestions.map(suggestionText).join(", ") || "Chưa có gợi ý sản phẩm",
              badge: `Thẻ ${i + 1}`,
              thumbPicker: (
                <ThumbPicker
                  label={c.title || "thẻ"}
                  src={c.image}
                  imageId={crop ? IMG : undefined}
                  crop={crop}
                  onPick={(url) => update({ image: url })}
                  onReset={() => update({ image: null })}
                  resetLabel="Gỡ ảnh"
                  width={64}
                  height={64}
                />
              ),
            };
          }}
          renderBody={(c, update) => (
            <Box>
              <Flex wrap="wrap" gap={16} align="flex-start">
                <Box style={{ flex: "0 1 240px", minWidth: 180 }}>
                  <ImagePicker
                    label="Ảnh thẻ"
                    src={c.image}
                    imageId={IMG}
                    crop={SAMPLE_CROPS.get(c.id)}
                    onPick={(url) => update({ image: url })}
                    onClear={c.image ? () => update({ image: null }) : undefined}
                    height={120}
                    maxWidth={240}
                  />
                </Box>
                <SimpleGrid
                  cols={{ base: 1, sm: 2 }}
                  spacing={12}
                  style={{ flex: "1 1 380px", minWidth: 0 }}
                >
                  <TextInput
                    label="Nhóm khách hàng"
                    value={c.title}
                    onChange={(e) => update({ title: e.currentTarget.value })}
                  />
                  <IconPicker
                    label="Icon"
                    value={c.icon}
                    onChange={(value) => update({ icon: value })}
                  />
                  <TagsInput
                    label="Gạch đầu dòng"
                    maxTags={3}
                    value={c.bullets}
                    onChange={(value) => update({ bullets: value })}
                  />
                  <TextInput
                    label="Nhãn nút"
                    value={c.buttonLabel}
                    onChange={(e) => update({ buttonLabel: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Link"
                    value={c.link}
                    onChange={(e) => update({ link: e.currentTarget.value })}
                  />
                </SimpleGrid>
              </Flex>
              <Text fz={14} fw={500} mt={16} mb={6}>
                Gợi ý sản phẩm (tối đa {MAX_SUGGESTIONS})
              </Text>
              <EditableList
                items={c.suggestions}
                onChange={suggestionSetter(c.id)}
                itemLabel={suggestionText}
                columns={[
                  { label: "Icon", width: "170px" },
                  { label: "Danh mục", width: "minmax(0, 1fr)" },
                  { label: "Tên hiển thị", width: "minmax(0, 1fr)" },
                ]}
                renderCells={(s, updateSuggestion) => (
                  <>
                    <IconPicker
                      ariaLabel={`Icon gợi ý ${suggestionText(s)}`}
                      value={s.icon}
                      onChange={(value) => updateSuggestion({ icon: value })}
                    />
                    <CategoryTreeSelect
                      ariaLabel={`Danh mục của gợi ý ${suggestionText(s)}`}
                      value={s.category.id}
                      fallbackName={s.category.name}
                      onChange={(id, node) =>
                        updateSuggestion({
                          category: node ? { id: node.id, name: node.name } : { id, name: "" },
                        })
                      }
                    />
                    <TextInput
                      aria-label="Tên hiển thị của gợi ý"
                      placeholder={s.category.name || "Tên danh mục"}
                      value={s.label}
                      onChange={(e) => updateSuggestion({ label: e.currentTarget.value })}
                    />
                  </>
                )}
                newItem={emptySuggestion}
                addLabel="Thêm gợi ý"
                max={MAX_SUGGESTIONS}
                minWidth={560}
              />
            </Box>
          )}
          newItem={(): PersonaCard => ({
            id: newId(),
            title: "",
            image: null,
            icon: null,
            bullets: [],
            suggestions: [],
            buttonLabel: "Khám phá ngay",
            link: "",
          })}
          addLabel="Thêm thẻ"
          max={8}
        />
      </SectionCard>
    </EditorLayout>
  );
};

export default EditPersona;
